import React, { useMemo, useState } from "react";
import { BOARD_LABELS } from "../../../app/utils/projectBoard/boardDesignTokens";
import {
  addCustomLabel,
  getCustomLabels,
  getLabelColorOptions,
} from "../../../app/utils/projectBoard/boardCustomLabels";
import { BoardButton } from "../../atoms/BoardDesignSystem";
import "./BoardLabelPicker.scss";

const BoardLabelPicker = ({
  boardId,
  selectedIds = [],
  onChange,
  disabled = false,
  onClose,
}) => {
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColorId, setNewColorId] = useState("green");
  const [customLabels, setCustomLabels] = useState(() => getCustomLabels(boardId));

  const allLabels = useMemo(
    () => [...BOARD_LABELS, ...customLabels],
    [customLabels]
  );

  const filteredLabels = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return allLabels;
    return allLabels.filter((label) => label.name.toLowerCase().includes(query));
  }, [allLabels, search]);

  const toggle = (labelId) => {
    if (disabled) return;
    const set = new Set(selectedIds);
    if (set.has(labelId)) set.delete(labelId);
    else set.add(labelId);
    onChange?.([...set]);
  };

  const handleCreateLabel = () => {
    const name = newName.trim();
    if (!name || !boardId || disabled) return;

    const label = addCustomLabel(boardId, { name, colorId: newColorId });
    if (!label) return;

    setCustomLabels(getCustomLabels(boardId));
    const next = [...new Set([...selectedIds, label.id])];
    onChange?.(next);
    setCreating(false);
    setNewName("");
    setNewColorId("green");
    setSearch("");
  };

  const colorOptions = getLabelColorOptions();

  return (
    <div className="board-label-picker" onClick={(e) => e.stopPropagation()}>
      <div className="board-label-picker__header">
        <h4 className="board-label-picker__title">Labels</h4>
        {onClose && (
          <button type="button" className="board-label-picker__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        )}
      </div>

      {!creating ? (
        <>
          <input
            type="search"
            className="board-label-picker__search"
            placeholder="Search labels..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={disabled}
          />

          <p className="board-label-picker__section-title">Labels</p>
          <div className="board-label-picker__list">
            {filteredLabels.map((label) => {
              const active = selectedIds.includes(label.id);
              return (
                <button
                  key={label.id}
                  type="button"
                  className={`board-label-picker__row ${active ? "board-label-picker__row--active" : ""}`}
                  onClick={() => toggle(label.id)}
                  disabled={disabled}
                >
                  <span
                    className={`board-label-picker__check ${active ? "board-label-picker__check--checked" : ""}`}
                    aria-hidden="true"
                  >
                    {active && <span className="board-label-picker__check-icon">✓</span>}
                  </span>
                  <span
                    className="board-label-picker__color"
                    style={{ backgroundColor: label.color }}
                    title={label.name}
                  >
                    <span className="board-label-picker__color-text">{label.name}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <BoardButton
            variant="secondary"
            block
            size="sm"
            onClick={() => setCreating(true)}
            disabled={disabled || !boardId}
          >
            Create a new label
          </BoardButton>
        </>
      ) : (
        <div className="board-label-picker__create">
          <p className="board-label-picker__section-title">Create label</p>
          <input
            type="text"
            className="board-label-picker__search"
            placeholder="Label name..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={disabled}
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleCreateLabel()}
          />
          <p className="board-label-picker__section-title">Select a color</p>
          <div className="board-label-picker__colors">
            {colorOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`board-label-picker__color-swatch ${newColorId === option.id ? "board-label-picker__color-swatch--active" : ""}`}
                style={{ backgroundColor: option.color }}
                onClick={() => setNewColorId(option.id)}
                aria-label={`Color ${option.id}`}
              />
            ))}
          </div>
          <div className="board-label-picker__create-actions">
            <BoardButton variant="primary" size="sm" onClick={handleCreateLabel} disabled={disabled || !newName.trim()}>
              Create
            </BoardButton>
            <BoardButton
              variant="ghost-dark"
              size="sm"
              onClick={() => {
                setCreating(false);
                setNewName("");
              }}
            >
              Cancel
            </BoardButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardLabelPicker;
